import { useEffect, useRef, useState } from 'react';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Code2,
  Italic,
  Link2,
  List,
  ListOrdered,
  RemoveFormatting,
  Type,
  Underline,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';

interface RichEmailTemplateEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholders: string[];
}

function safeExec(command: string, value?: string) {
  if (typeof document === 'undefined') return;
  document.execCommand(command, false, value);
}

const TEXT_SIZE_OPTIONS = [
  { label: 'Pequeño', value: '13px' },
  { label: 'Normal', value: '15px' },
  { label: 'Grande', value: '18px' },
] as const;

export function RichEmailTemplateEditor({
  value,
  onChange,
  placeholders,
}: RichEmailTemplateEditorProps) {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const selectionRef = useRef<Range | null>(null);
  const [viewMode, setViewMode] = useState<'visual' | 'source'>('visual');

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    if (editor.innerHTML !== value) {
      editor.innerHTML = value || '<p></p>';
    }
  }, [value]);

  useEffect(() => {
    if (viewMode !== 'visual') return;

    const frame = window.requestAnimationFrame(() => {
      const editor = editorRef.current;
      if (!editor) return;

      if (editor.innerHTML !== value) {
        editor.innerHTML = value || '<p></p>';
      }
    });

    return () => window.cancelAnimationFrame(frame);
  }, [value, viewMode]);

  const syncValue = () => {
    const editor = editorRef.current;
    if (!editor) return;
    onChange(editor.innerHTML);
  };

  const saveSelection = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    if (!editorRef.current?.contains(range.commonAncestorContainer)) return;
    selectionRef.current = range.cloneRange();
  };

  const restoreSelection = () => {
    const selection = window.getSelection();
    if (!selection || !selectionRef.current) return;
    selection.removeAllRanges();
    selection.addRange(selectionRef.current);
  };

  const runCommand = (command: string, commandValue?: string) => {
    editorRef.current?.focus();
    restoreSelection();
    safeExec(command, commandValue);
    syncValue();
    saveSelection();
  };

  const applyTextSize = (fontSize: string) => {
    const editor = editorRef.current;
    if (!editor) return;

    editor.focus();
    restoreSelection();

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    if (range.collapsed || !editor.contains(range.commonAncestorContainer)) return;

    const span = document.createElement('span');
    span.style.fontSize = fontSize;
    span.appendChild(range.extractContents());
    range.insertNode(span);
    range.selectNodeContents(span);
    selection.removeAllRanges();
    selection.addRange(range);
    selectionRef.current = range.cloneRange();
    syncValue();
  };

  const insertPlaceholder = (placeholder: string) => {
    const editor = editorRef.current;
    if (!editor) return;

    editor.focus();
    restoreSelection();

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      editor.innerHTML += placeholder;
      syncValue();
      return;
    }

    const range = selection.getRangeAt(0);
    range.deleteContents();
    const node = document.createTextNode(placeholder);
    range.insertNode(node);
    range.setStartAfter(node);
    range.setEndAfter(node);
    selection.removeAllRanges();
    selection.addRange(range);
    selectionRef.current = range.cloneRange();
    syncValue();
  };

  const addLink = () => {
    const href = window.prompt('Ingresa la URL del enlace seguro', 'https://');
    if (!href) return;
    runCommand('createLink', href);
  };

  const handleViewModeChange = (nextViewMode: 'visual' | 'source') => {
    if (nextViewMode === 'source') {
      syncValue();
      saveSelection();
      setViewMode(nextViewMode);
      return;
    }

    setViewMode(nextViewMode);
  };

  return (
    <div className="space-y-3">
      <Tabs value={viewMode} onValueChange={(value) => handleViewModeChange(value as 'visual' | 'source')} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="visual">Editor visual</TabsTrigger>
          <TabsTrigger value="source">HTML (solo lectura)</TabsTrigger>
        </TabsList>

        <TabsContent value="visual" forceMount className="space-y-3 data-[state=inactive]:hidden">
          <div className="flex flex-wrap gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
            <Button type="button" variant="outline" size="sm" onClick={() => runCommand('bold')}>
              <Bold className="mr-2 h-4 w-4" />
              Negrita
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => runCommand('italic')}>
              <Italic className="mr-2 h-4 w-4" />
              Cursiva
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => runCommand('underline')}>
              <Underline className="mr-2 h-4 w-4" />
              Subrayado
            </Button>
            {TEXT_SIZE_OPTIONS.map((option) => (
              <Button
                key={option.value}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => applyTextSize(option.value)}
              >
                <Type className="mr-2 h-4 w-4" />
                {option.label}
              </Button>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => runCommand('justifyLeft')}>
              <AlignLeft className="mr-2 h-4 w-4" />
              Izquierda
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => runCommand('justifyCenter')}>
              <AlignCenter className="mr-2 h-4 w-4" />
              Centrado
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => runCommand('justifyRight')}>
              <AlignRight className="mr-2 h-4 w-4" />
              Derecha
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => runCommand('insertUnorderedList')}>
              <List className="mr-2 h-4 w-4" />
              Lista
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => runCommand('insertOrderedList')}>
              <ListOrdered className="mr-2 h-4 w-4" />
              Lista numerada
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={addLink}>
              <Link2 className="mr-2 h-4 w-4" />
              Enlace
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => runCommand('removeFormat')}>
              <RemoveFormatting className="mr-2 h-4 w-4" />
              Limpiar formato
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => runCommand('insertParagraph')}>
              Párrafo
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => runCommand('insertHTML', '<br />')}>
              Salto de línea
            </Button>
          </div>

          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            className="min-h-[320px] rounded-xl border border-slate-200 bg-white px-4 py-3 text-[15px] leading-7 text-slate-800 shadow-inner focus:outline-none focus:ring-2 focus:ring-[#1e66d0]/30"
            onInput={syncValue}
            onBlur={() => {
              syncValue();
              saveSelection();
            }}
            onKeyUp={saveSelection}
            onMouseUp={saveSelection}
          />

          <div className="space-y-2">
            <p className="text-sm font-medium text-slate-700">Insertar placeholder</p>
            <div className="flex flex-wrap gap-2">
              {placeholders.map((placeholder) => (
                <Button
                  key={placeholder}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                  onClick={() => insertPlaceholder(`{{${placeholder}}}`)}
                >
                  {`{{${placeholder}}}`}
                </Button>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="source" forceMount className="data-[state=inactive]:hidden">
          <div className="rounded-xl border bg-slate-950 p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-medium text-slate-200">
              <Code2 className="h-4 w-4" />
              Vista técnica secundaria
            </div>
            <pre className="whitespace-pre-wrap break-words text-xs leading-6 text-slate-100">{value}</pre>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
